'use client'

import React, {useMemo} from 'react'

import './_material-image.scss'

import cn from '../../libs/class-names'
import * as colorService from '../../services/color'

import {CommonProps, Overwrite} from '../../types'

type Props<T extends React.ElementType> = Overwrite<
  CommonProps<T>,
  {
    as?: T
    color?: string
    image?: string
    label?: string
    square?: boolean
    border?: boolean
    disabled?: boolean
    size?: 'default' | 'l' | 's' | 'xs'
  }
>

const shouldHaveBorder = (hex: string) =>
  colorService.isValid(hex) && colorService.contrast(hex, '#ffffff') < 1.25

const MaterialImage = <T extends React.ElementType = 'span'>({
  className,
  as,
  color = '#ffffff',
  image,
  label,
  square = false,
  border = false,
  disabled = false,
  size = 'default',
  ...props
}: Props<T>) => {
  const Root = (as ?? 'span') as React.ElementType

  const hasBorder = useMemo(() => border && shouldHaveBorder(color), [border, color])
  const style: Record<string, string> = {
    backgroundColor: color,
  }

  if (image) {
    style.backgroundImage = `url(${image})`
  }

  return (
    <Root
      type={Root === 'button' ? 'button' : undefined}
      {...props}
      className={cn(
        'MaterialImage',
        {border: hasBorder, square, disabled, [`size-${size}`]: true},
        className
      )}
      style={style}
      aria-label={label}
    />
  )
}

export default MaterialImage
